/**
 * Componente ConfiguracionRetenciones
 * Configuración de retenciones (opcional)
 */

import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  FormControlLabel,
  Checkbox,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  Alert,
} from '@mui/material';
import { Delete, Add } from '@mui/icons-material';

const ConfiguracionRetenciones = ({
  aplicar,
  retenciones = [],
  onRetencionesCambio,
  codigosRetenciones = [],
  total = 0,
  onAplicarCambio,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [nuevaRetencion, setNuevaRetencion] = useState({
    codigo: '',
    porcentaje: 0,
    base_imponible: 0,
    valor: 0,
    tipo: 'RENTA',
  });

  const handleAplicarChange = (e) => {
    onAplicarCambio(e.target.checked);
  };

  const handleAgregarRetencion = () => {
    setNuevaRetencion({
      codigo: '',
      porcentaje: 0,
      base_imponible: total,
      valor: 0,
      tipo: 'RENTA',
    });
    setDialogOpen(true);
  };

  const handleCodigoChange = (e) => {
    const codigo = e.target.value;
    const codigoInfo = codigosRetenciones.find((c) => c.codigo === codigo);
    if (codigoInfo) {
      const porcentaje = parseFloat(codigoInfo.porcentaje || 0);
      const baseImponible = total;
      const valor = (baseImponible * porcentaje) / 100;
      setNuevaRetencion({
        ...nuevaRetencion,
        codigo,
        porcentaje,
        base_imponible: baseImponible,
        valor: parseFloat(valor.toFixed(2)),
        tipo: codigoInfo.tipo || 'RENTA',
        descripcion: codigoInfo.descripcion || '',
      });
    }
  };

  const handleGuardarRetencion = () => {
    const retencionesActualizadas = [...retenciones, { ...nuevaRetencion, id: Date.now() }];
    onRetencionesCambio(retencionesActualizadas);
    setDialogOpen(false);
  };

  const handleEliminarRetencion = (id) => {
    const retencionesActualizadas = retenciones.filter((r) => r.id !== id);
    onRetencionesCambio(retencionesActualizadas);
  };

  const totalRetenciones = retenciones.reduce((sum, r) => sum + parseFloat(r.valor || 0), 0);

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Paso 5: Configuración de Retenciones
      </Typography>

      <FormControlLabel
        control={<Checkbox checked={aplicar} onChange={handleAplicarChange} />}
        label="¿Aplicar retenciones a esta compra?"
      />

      {aplicar && (
        <Box sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={handleAgregarRetencion}
            sx={{ mb: 2 }}
          >
            Agregar Retención
          </Button>

          {retenciones.length > 0 && (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Código</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Descripción</TableCell>
                    <TableCell align="right">Porcentaje</TableCell>
                    <TableCell align="right">Base Imponible</TableCell>
                    <TableCell align="right">Valor</TableCell>
                    <TableCell align="center">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {retenciones.map((ret) => (
                    <TableRow key={ret.id}>
                      <TableCell>{ret.codigo}</TableCell>
                      <TableCell>{ret.tipo}</TableCell>
                      <TableCell>{ret.descripcion || '-'}</TableCell>
                      <TableCell align="right">{ret.porcentaje}%</TableCell>
                      <TableCell align="right">${parseFloat(ret.base_imponible).toFixed(2)}</TableCell>
                      <TableCell align="right">${parseFloat(ret.valor).toFixed(2)}</TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleEliminarRetencion(ret.id)}
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={5} align="right">
                      <strong>Total Retenciones:</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>${totalRetenciones.toFixed(2)}</strong>
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {retenciones.length === 0 && (
            <Alert severity="info">
              No se han agregado retenciones. Haga clic en "Agregar Retención" para configurar.
            </Alert>
          )}
        </Box>
      )}

      {/* Dialog para agregar retención */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Agregar Retención</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              select
              label="Código de Retención"
              value={nuevaRetencion.codigo}
              onChange={handleCodigoChange}
              fullWidth
            >
              {codigosRetenciones.map((codigo) => (
                <MenuItem key={codigo.codigo} value={codigo.codigo}>
                  {codigo.codigo} - {codigo.descripcion} ({codigo.porcentaje}%)
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Tipo"
              value={nuevaRetencion.tipo}
              disabled
              fullWidth
            />

            <TextField
              label="Porcentaje"
              type="number"
              value={nuevaRetencion.porcentaje}
              disabled
              fullWidth
            />

            <TextField
              label="Base Imponible"
              type="number"
              value={nuevaRetencion.base_imponible}
              onChange={(e) =>
                setNuevaRetencion({
                  ...nuevaRetencion,
                  base_imponible: parseFloat(e.target.value),
                  valor: parseFloat(((parseFloat(e.target.value) * nuevaRetencion.porcentaje) / 100).toFixed(2)),
                })
              }
              fullWidth
            />

            <TextField
              label="Valor Retención"
              type="number"
              value={nuevaRetencion.valor}
              disabled
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleGuardarRetencion}
            disabled={!nuevaRetencion.codigo || nuevaRetencion.valor <= 0}
          >
            Agregar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ConfiguracionRetenciones;
